import { TypeCampaignEnum } from '@/types';
import type { FlowStructureDto } from '@/types/flow-structure';
import type { FlowSettings, FlowSettingsAutomation, FlowSettingsBulk } from '@/types/flow-settings';
import { coerceFlowSettings, getDefaultAutomationSettings, getDefaultBulkSettings } from '@/types/flow-settings';
import { getLocalStorageElement, setLocalStorageElement, removeLocalStorageElement } from '@/helpers/LocalStorage';
import { checkVersionConflict } from '@/composables/flows/useFlowApi';
import type { FlowHistoryState, HistoryEntry } from '@/composables/flows/useFlowHistory';

const STORAGE_KEY = 'flows';

export enum EditingTemplateStatusEnum {
  IN_TEMPLATE_CHOOSER = 'IN_TEMPLATE_CHOOSER',
  IN_TEMPLATE_EDITOR = 'IN_TEMPLATE_EDITOR',
  SAVE_STAY_TEMPLATE_EDITOR = 'SAVE_STAY_TEMPLATE_EDITOR',
  SAVE_QUIT_TEMPLATE_EDITOR = 'SAVE_QUIT_TEMPLATE_EDITOR',
  QUIT_TEMPLATE_EDITOR = 'QUIT_TEMPLATE_EDITOR',
}

export enum EditingTemplateTypeEnum {
  EDIT = 'EDIT',
  CHANGE = 'CHANGE',
}

export interface EditingTemplateInformations {
  subject?: Record<string, string>;
  fromName?: Record<string, string>;
  fromEmail?: Record<string, string>;
  previewText?: Record<string, string>;
}

export interface EditingTemplate {
  status: EditingTemplateStatusEnum;
  id_campaign: number;
  type?: EditingTemplateTypeEnum;
  isRedirect?: boolean;
  oldIdTemplate?: number;
  parentsRedirect?: Array<number>;
  idTemplate?: number;
  informations?: EditingTemplateInformations;
}

export interface FlowIdentifier {
  id: number;
  type: TypeCampaignEnum;
}

export interface OpenFlow {
  id: number;
  type: TypeCampaignEnum;
  flowData: FlowStructureDto;
  settings: FlowSettings;
  isDirty: boolean;
  isMinimized: boolean;
  lastModified: Date;
  name: string;
  history?: FlowHistoryState;
  isPreviewMode?: boolean;
}

export interface FlowVersionConflict {
  flowId: number;
  flowType: TypeCampaignEnum;
  flowName: string;
  localDate: string;
  remoteDate: string;
  remoteFlow: FlowStructureDto;
}

// Generate a unique key for the flows record based on id and type
export const getFlowKey = (id: number, type: TypeCampaignEnum): string => `${type}-${id}`;

/**
 * Save minimized flows to localStorage
 */
const saveMinimizedFlowsToStorage = (flows: Record<string, OpenFlow>): void => {
  const minimizedFlows = Object.values(flows).filter((f) => f.isMinimized);
  if (minimizedFlows.length === 0) {
    removeLocalStorageElement(STORAGE_KEY);
    return;
  }

  const dataToStore = minimizedFlows.map((f) => ({
    id: f.id,
    type: f.type,
    flowData: f.flowData,
    settings: f.settings,
    isDirty: f.isDirty,
    name: f.name,
    lastModified: f.lastModified.toISOString(),
    history: f.history,
  }));

  setLocalStorageElement(STORAGE_KEY, JSON.stringify(dataToStore));
};

/**
 * Load minimized flows from localStorage
 */
const loadMinimizedFlowsFromStorage = (): Record<string, OpenFlow> => {
  const stored = getLocalStorageElement(STORAGE_KEY);
  if (!stored) return {};

  try {
    const parsed = JSON.parse(stored);
    const flows: Record<string, OpenFlow> = {};

    for (const item of parsed) {
      const key = getFlowKey(item.id, item.type);
      // Get default settings based on type if not stored
      const defaultSettings = item.type === TypeCampaignEnum.AUTOMATION
        ? getDefaultAutomationSettings(item.name)
        : getDefaultBulkSettings(item.name);
      flows[key] = {
        id: item.id,
        type: item.type,
        flowData: item.flowData,
        // Coerce in case the localStorage entry was written before the
        // normalisation existed (legacy string binaries).
        settings: coerceFlowSettings(item.settings || defaultSettings),
        isDirty: item.isDirty,
        isMinimized: true,
        name: item.name,
        lastModified: new Date(item.lastModified),
        history: item.history,
      };
    }

    return flows;
  } catch (e) {
    console.error('[FLOW_EDITOR] Error loading minimized flows from storage:', e);
    return {};
  }
};

export interface FlowEditorState {
  flows: Record<string, OpenFlow>;
  activeFlow: FlowIdentifier | null;
  editingTemplate: EditingTemplate | null;
  isEditorVisible: boolean;
  versionConflict: FlowVersionConflict | null;
}

export default {
  namespaced: true,

  state: (): FlowEditorState => ({
    flows: {},
    activeFlow: null,
    editingTemplate: null,
    isEditorVisible: false,
    versionConflict: null,
  }),

  getters: {
    getFlows: (state: FlowEditorState) => state.flows,
    getActiveFlowIdentifier: (state: FlowEditorState) => state.activeFlow,
    getEditingTemplate: (state: FlowEditorState) => state.editingTemplate,
    isEditorVisible: (state: FlowEditorState) => state.isEditorVisible,
    getActiveFlow: (state: FlowEditorState) => {
      if (!state.activeFlow) return null;
      const key = getFlowKey(state.activeFlow.id, state.activeFlow.type);
      return state.flows[key] || null;
    },
    getFlowByKey: (state: FlowEditorState) => (key: string) => state.flows[key] || null,
    getFlowById: (state: FlowEditorState) => (id: number, type: TypeCampaignEnum) => {
      const key = getFlowKey(id, type);
      return state.flows[key] || null;
    },
    getOpenFlowsList: (state: FlowEditorState) => Object.values(state.flows),
    getMinimizedFlows: (state: FlowEditorState) => (
      Object.values(state.flows).filter((s) => s.isMinimized)
    ),
    hasUnsavedChanges: (state: FlowEditorState) => (
      Object.values(state.flows).some((s) => s.isDirty)
    ),
    isFlowOpen: (state: FlowEditorState) => (id: number, type: TypeCampaignEnum) => {
      const key = getFlowKey(id, type);
      return key in state.flows;
    },
    getActiveFlowSettings: (state: FlowEditorState) => {
      if (!state.activeFlow) return null;
      const key = getFlowKey(state.activeFlow.id, state.activeFlow.type);
      return state.flows[key]?.settings || null;
    },
    getVersionConflict: (state: FlowEditorState) => state.versionConflict,
  },

  mutations: {
    setFlow(state: FlowEditorState, flow: OpenFlow) {
      const key = getFlowKey(flow.id, flow.type);
      // Single chokepoint for settings normalisation — protects against any
      // caller (action, localStorage restore, viewer, conflict resolution)
      // that hands us raw API-shape settings with string binaries.
      state.flows[key] = {
        ...flow,
        settings: flow.settings ? coerceFlowSettings(flow.settings) : flow.settings,
      };
    },

    removeFlow(state: FlowEditorState, { id, type }: FlowIdentifier) {
      const key = getFlowKey(id, type);
      delete state.flows[key];
    },

    setActiveFlow(state: FlowEditorState, activeFlow: FlowIdentifier | null) {
      state.activeFlow = activeFlow;
    },

    updateFlowData(state: FlowEditorState, { id, type, flowData }: { id: number; type: TypeCampaignEnum; flowData: FlowStructureDto }) {
      const key = getFlowKey(id, type);
      const flow = state.flows[key];
      if (flow) {
        flow.flowData = flowData;
        flow.isDirty = true;
        flow.lastModified = new Date();
      }
    },

    updateFlowName(state: FlowEditorState, { id, type, name }: { id: number; type: TypeCampaignEnum; name: string }) {
      const key = getFlowKey(id, type);
      const flow = state.flows[key];
      if (flow) {
        flow.name = name;
        // Also update name in settings
        if (flow.settings) {
          flow.settings.name = name;
        }
        flow.isDirty = true;
        flow.lastModified = new Date();
      }
    },

    updateFlowSettings(state: FlowEditorState, { id, type, settings }: { id: number; type: TypeCampaignEnum; settings: Partial<FlowSettings> }) {
      const key = getFlowKey(id, type);
      const flow = state.flows[key];
      if (flow) {
        flow.settings = { ...flow.settings, ...settings } as FlowSettings;
        // Sync name if it was updated in settings
        if (settings.name) {
          flow.name = settings.name;
        }
        // Mirror settings into flowData.settings so consumers watching flowData
        // (e.g. FlowRenderer's calculateTargetBulk watcher) react to the change.
        // We replace flowData with a new object so the deep-watch fires reliably.
        if (flow.flowData) {
          flow.flowData = {
            ...flow.flowData,
            settings: { ...(flow.flowData.settings ?? {}), ...settings },
          };
        }
        flow.isDirty = true;
        flow.lastModified = new Date();
      }
    },

    setFlowMinimized(state: FlowEditorState, { id, type, isMinimized }: { id: number; type: TypeCampaignEnum; isMinimized: boolean }) {
      const key = getFlowKey(id, type);
      const flow = state.flows[key];
      if (flow) {
        flow.isMinimized = isMinimized;
      }
    },

    setFlowDirty(state: FlowEditorState, { id, type, isDirty }: { id: number; type: TypeCampaignEnum; isDirty: boolean }) {
      const key = getFlowKey(id, type);
      const flow = state.flows[key];
      if (flow) {
        flow.isDirty = isDirty;
        flow.lastModified = new Date();
      }
    },

    updateFlowStatus(state: FlowEditorState, { id, type, status }: { id: number; type: TypeCampaignEnum; status: string }) {
      const key = getFlowKey(id, type);
      const flow = state.flows[key];
      if (flow && flow.flowData) {
        flow.flowData.status = status;
      }
    },

    clearAllFlows(state: FlowEditorState) {
      state.flows = {};
      state.activeFlow = null;
      state.editingTemplate = null;
    },

    setEditingTemplate(state: FlowEditorState, editingTemplate: EditingTemplate | null) {
      state.editingTemplate = editingTemplate;
    },

    setEditorVisible(state: FlowEditorState, isVisible: boolean) {
      state.isEditorVisible = isVisible;
    },

    setVersionConflict(state: FlowEditorState, conflict: FlowVersionConflict | null) {
      state.versionConflict = conflict;
    },

    updateFlowHistory(state: FlowEditorState, { id, type, history }: { id: number; type: TypeCampaignEnum; history: FlowHistoryState }) {
      const key = getFlowKey(id, type);
      const flow = state.flows[key];
      if (flow) {
        flow.history = history;
      }
    },

    clearFlowHistory(state: FlowEditorState, { id, type }: FlowIdentifier) {
      const key = getFlowKey(id, type);
      const flow = state.flows[key];
      if (flow) {
        flow.history = undefined;
      }
    },

    updateFlowId(state: FlowEditorState, { oldId, newId, type }: { oldId: number; newId: number; type: TypeCampaignEnum }) {
      const oldKey = getFlowKey(oldId, type);
      const flow = state.flows[oldKey];
      if (flow) {
        // Update the flow's id
        flow.id = newId;
        // Also update the campaignId in flowData
        if (flow.flowData) {
          flow.flowData.campaignId = newId;
        }
        // Move to new key
        const newKey = getFlowKey(newId, type);
        state.flows[newKey] = flow;
        delete state.flows[oldKey];
        // Update activeFlow if it was the one being updated
        if (state.activeFlow?.id === oldId && state.activeFlow?.type === type) {
          state.activeFlow = { id: newId, type };
        }
      }
    },
  },

  actions: {
    async openFlow(
      { commit, state, dispatch }: { commit: Function; state: FlowEditorState; dispatch: Function },
      {
        id, flowData, type, name = '', settings, isPreviewMode = false,
      }: { id: number; flowData: FlowStructureDto; type: TypeCampaignEnum; name?: string; settings?: FlowSettings; isPreviewMode?: boolean },
    ) {
      const key = getFlowKey(id, type);
      // If already open, check for version conflicts before restoring
      if (state.flows[key]) {
        await dispatch('restoreFlowWithConflictCheck', { id, type });
        return;
      }

      // Get default settings based on type if not provided
      const defaultSettings = type === TypeCampaignEnum.AUTOMATION
        ? getDefaultAutomationSettings(name)
        : getDefaultBulkSettings(name);

      // Extract history from flowData and convert to FlowHistoryState
      const { history: historyEntries, ...flowDataWithoutHistory } = flowData;
      const historyState: FlowHistoryState | undefined = historyEntries && historyEntries.length > 0
        ? { undoStack: historyEntries as HistoryEntry[], redoStack: [] }
        : undefined;

      const flow: OpenFlow = {
        id,
        type,
        flowData: flowDataWithoutHistory as FlowStructureDto,
        settings: settings || defaultSettings,
        isDirty: false,
        isMinimized: false,
        lastModified: new Date(),
        name,
        history: historyState,
        isPreviewMode,
      };

      commit('setFlow', flow);
      commit('setActiveFlow', { id, type });
      commit('setEditorVisible', true);
    },

    minimizeFlow(
      { commit, state }: { commit: Function; state: FlowEditorState },
      { id, type }: FlowIdentifier,
    ) {
      commit('setFlowMinimized', { id, type, isMinimized: true });
      if (state.activeFlow?.id === id && state.activeFlow?.type === type) {
        commit('setActiveFlow', null);
      }
      // Persist to localStorage
      saveMinimizedFlowsToStorage(state.flows);
    },

    restoreFlow(
      { commit, state }: { commit: Function; state: FlowEditorState },
      { id, type }: FlowIdentifier,
    ) {
      commit('setFlowMinimized', { id, type, isMinimized: false });
      commit('setActiveFlow', { id, type });
      commit('setEditorVisible', true);
      // Update localStorage (flow is no longer minimized)
      saveMinimizedFlowsToStorage(state.flows);
    },

    async restoreFlowWithConflictCheck(
      { commit, state, dispatch }: { commit: Function; state: FlowEditorState; dispatch: Function },
      { id, type }: FlowIdentifier,
    ) {
      const key = getFlowKey(id, type);
      const flowToRestore = state.flows[key];

      if (!flowToRestore) {
        dispatch('restoreFlow', { id, type });
        return;
      }

      // Only check for version conflict if there are local unsaved changes
      // This prevents false conflicts when status changes (e.g., BEING_EDITED) update dateModification
      if (!flowToRestore.isDirty) {
        dispatch('restoreFlow', { id, type });
        return;
      }

      const localDateModification = flowToRestore.flowData?.dateModification;

      if (localDateModification && (type === TypeCampaignEnum.AUTOMATION || type === TypeCampaignEnum.BULK)) {
        const conflictResult = await checkVersionConflict(
          type,
          id,
          localDateModification,
        );

        if (conflictResult.hasConflict && conflictResult.remoteFlow) {
          // Store conflict in state - a watcher in a component will display the modal
          commit('setVersionConflict', {
            flowId: id,
            flowType: type,
            flowName: flowToRestore.name,
            localDate: conflictResult.localDate,
            remoteDate: conflictResult.remoteDate,
            remoteFlow: conflictResult.remoteFlow,
          });
          return;
        }
      }

      // No conflict, restore normally
      dispatch('restoreFlow', { id, type });
    },

    closeFlow(
      { commit, state }: { commit: Function; state: FlowEditorState },
      { id, type }: FlowIdentifier,
    ) {
      commit('removeFlow', { id, type });
      // Always update localStorage to remove the flow if it was stored
      saveMinimizedFlowsToStorage(state.flows);

      if (state.activeFlow?.id === id && state.activeFlow?.type === type) {
        // Only activate a non-minimized flow, don't auto-open minimized ones
        const remainingNonMinimizedFlows = Object.values(state.flows).filter((f) => !f.isMinimized);
        if (remainingNonMinimizedFlows.length > 0) {
          const first = remainingNonMinimizedFlows[0];
          commit('setActiveFlow', { id: first.id, type: first.type });
        } else {
          commit('setActiveFlow', null);
          commit('setEditorVisible', false);
        }
      }
    },

    markFlowAsSaved(
      { commit }: { commit: Function },
      { id, type }: FlowIdentifier,
    ) {
      commit('setFlowDirty', { id, type, isDirty: false });
    },

    updateFlowIdAfterSave(
      { commit, state }: { commit: Function; state: FlowEditorState },
      { oldId, newId, type }: { oldId: number; newId: number; type: TypeCampaignEnum },
    ) {
      if (oldId !== newId) {
        commit('updateFlowId', { oldId, newId, type });
        // Update localStorage if the flow was minimized
        saveMinimizedFlowsToStorage(state.flows);
      }
    },

    closeAllFlows({ commit }: { commit: Function }) {
      commit('clearAllFlows');
      // Clear localStorage
      removeLocalStorageElement(STORAGE_KEY);
    },

    loadFromStorage({ commit }: { commit: Function }) {
      const flows = loadMinimizedFlowsFromStorage();
      Object.values(flows).forEach((flow) => {
        commit('setFlow', flow);
      });
    },
  },
};
